import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Typography, 
  Chip,
  Divider,
  Button,
  Grid,
  Paper,
  Rating,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AttractionsIcon from '@mui/icons-material/Attractions';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import HotelIcon from '@mui/icons-material/Hotel';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import PublicIcon from '@mui/icons-material/Public';
import { Link as RouterLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getDestinationById, getDestinationByName, getDestinations } from '../services/destinationService';

// Generate more detailed content for a destination
const generateDetails = (destination) => {
  // Sample attractions based on destination
  const attractions = [
    {
      name: `${destination.name.split(',')[0]} Historical Museum`,
      description: "Learn about the rich cultural heritage and history of the region."
    },
    {
      name: `${destination.name.split(',')[0]} Central Park`,
      description: "Beautiful green space perfect for relaxation and outdoor activities."
    },
    {
      name: `${destination.name.split(',')[0]} Cathedral`,
      description: "Stunning architectural masterpiece with historical significance."
    },
    {
      name: "Local Market",
      description: "Experience the vibrant local culture and find unique souvenirs."
    }
  ];

  // Sample dining options
  const dining = [
    {
      name: "Traditional Restaurant",
      description: `Authentic ${destination.name.split(',')[0]} cuisine in a charming setting.`
    },
    {
      name: "Waterfront Café",
      description: "Casual dining with beautiful views and light meals."
    },
    {
      name: "Gourmet Experience",
      description: "Fine dining showcasing the best local ingredients and techniques."
    }
  ];

  // Sample accommodations
  const accommodations = [
    {
      name: "Luxury Resort & Spa",
      description: "5-star accommodations with full amenities and exceptional service."
    },
    {
      name: "Boutique Hotel",
      description: "Charming mid-range option with unique character and personal service."
    },
    {
      name: "Budget-Friendly Hostel",
      description: "Clean, comfortable, and affordable lodging for budget travelers."
    }
  ];

  return {
    description: `${destination.description} This ${destination.tags.includes('City') ? 'city' : 'destination'} offers a perfect blend of cultural experiences, natural beauty, and unforgettable memories for travelers of all types.`,
    attractions,
    dining,
    accommodations,
    travelTips: [
      `The currency used in ${destination.name} is the local currency. Exchange rates are favorable at official exchange offices.`,
      `${destination.name} is generally safe, but as with any travel destination, keep your belongings secure and stay aware of your surroundings.`,
      `Learning a few basic phrases in the local language will enhance your experience and is appreciated by locals.`,
      `Public transportation is ${destination.tags.includes('City') ? 'extensive and easy to navigate' : 'available but may be limited in some areas'}.`,
      `Don't miss the opportunity to experience the local festivals if visiting during ${destination.bestTime}.`
    ]
  };
};

const DestinationDetailPage = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [destination, setDestination] = useState(null);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedDestinations, setRelatedDestinations] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const loadDestination = async () => {
      try {
        setLoading(true);
        // Check which parameter was provided (id or name)
        const { id, name } = params;
        let destinationData;
        
        if (id) {
          // Fetch destination by ID
          destinationData = await getDestinationById(id);
        } else if (name) {
          // Fetch destination by name
          try {
            const decodedName = decodeURIComponent(name);
            destinationData = await getDestinationByName(decodedName);
          } catch (err) {
            // If getting by name fails, fetch all destinations and filter
            const allDestinations = await getDestinations();
            const decodedName = decodeURIComponent(name);
            destinationData = allDestinations.find(dest => 
              dest.name.toLowerCase() === decodedName.toLowerCase() ||
              dest.name.toLowerCase().includes(decodedName.toLowerCase())
            );
            
            // Special case for Tokyo, Japan (handling like before)
            if (!destinationData && decodedName === "Tokyo, Japan") {
              destinationData = {
                id: 'tokyo-japan',
                name: 'Tokyo, Japan',
                image: 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?q=80&w=1888&auto=format&fit=crop',
                description: 'Explore the perfect blend of traditional culture and futuristic technology in Japan\'s bustling capital.',
                tags: ['Asia', 'City', 'Culture'],
                rating: 4.8,
                bestTime: 'March to May, September to November',
                priceLevel: '$$$'
              };
            }
          }
        }
        
        if (destinationData) {
          setDestination(destinationData);
          
          // Generate or use detailed content
          const detailedContent = generateDetails(destinationData);
          setDetails(detailedContent);
          
          // Fetch related destinations based on tags
          if (destinationData.tags && destinationData.tags.length > 0) {
            try {
              const allDestinations = await getDestinations();
              const related = allDestinations
                .filter(d => d.id !== destinationData.id && 
                  d.tags.some(tag => destinationData.tags.includes(tag)))
                .slice(0, 3);
              
              setRelatedDestinations(related);
            } catch (err) {
              console.error('Error fetching related destinations:', err);
              setRelatedDestinations([]);
            }
          }
          
          setError(null);
        } else {
          setError('Destination not found');
        }
      } catch (err) {
        console.error('Error loading destination:', err);
        setError('Failed to load destination details. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    loadDestination();
  }, [params]);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 600);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 10, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={isMobile ? 40 : 48} />
        </Box>
      </Container>
    );
  }
  
  if (error || !destination) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 10 }}>
          <Alert severity="error" sx={{ 
            mb: 4,
            '& .MuiAlert-message': {
              fontSize: { xs: '0.8rem', sm: '0.875rem' }
            }
          }}>
            {error || 'Destination not found'}
          </Alert>
          <Button 
            variant="contained" 
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/destinations')}
            sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
          >
            Back to Destinations
          </Button>
        </Box>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 6, mt: { xs: 8, md: 10 } }}>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Box mb={4}>
            <Button 
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{ 
                mb: 2,
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}
            >
              Back to Destinations
            </Button>
            
            {/* Tags */}
            <Box mb={2}>
              {destination.tags.map(tag => (
                <Chip 
                  key={tag} 
                  label={tag} 
                  component={RouterLink}
                  to={`/destinations?tag=${encodeURIComponent(tag)}`}
                  clickable
                  sx={{ 
                    mr: 1, 
                    mb: 1,
                    fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
                    height: { xs: '28px', sm: '32px' }
                  }} 
                />
              ))}
            </Box>
            
            {/* Title and Rating */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <Typography 
                variant="h3" 
                component="h1" 
                fontWeight="bold"
                gutterBottom
                sx={{ 
                  mb: 3,
                  fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' }
                }}
              >
                {destination.name}
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Rating value={destination.rating} precision={0.1} size="large" readOnly />
                <Typography variant="h6" sx={{ 
                  ml: 1,
                  fontSize: { xs: '1rem', sm: '1.25rem' }
                }}>
                  {destination.rating.toFixed(1)}
                </Typography>
              </Box>
            </Box>
            
            {/* Key Info */}
            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AccessTimeIcon fontSize="small" color="primary" sx={{ mr: 1, fontSize: { xs: '0.9rem', sm: '1.1rem' } }} />
                  <Typography variant="body1" sx={{ fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1rem' } }}>
                    <strong>Best time to visit:</strong> {destination.bestTime}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LocalOfferIcon fontSize="small" color="primary" sx={{ mr: 1, fontSize: { xs: '0.9rem', sm: '1.1rem' } }} />
                  <Typography variant="body1" sx={{ fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1rem' } }}>
                    <strong>Price Level:</strong> {destination.priceLevel}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
          
          {/* Featured Image */}
          <Box 
            sx={{ 
              height: { xs: 300, md: 500 }, 
              width: '100%', 
              backgroundImage: `url(${destination.image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderRadius: 2,
              mb: 5
            }} 
          />
          
          {/* Content */}
          <Grid container spacing={4}>
            <Grid item xs={12} md={8}>
              <Box sx={{ typography: 'body1' }}>
                {/* Description */}
                <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                  About {destination.name}
                </Typography>
                <Typography paragraph sx={{ mb: 4, fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1rem' } }}>
                  {details.description}
                </Typography>
                
                {/* Attractions */}
                <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                  Top Attractions
                </Typography>
                <List>
                  {details.attractions.map((attraction, index) => (
                    <ListItem key={index} sx={{ pl: 0 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <AttractionsIcon color="primary" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={attraction.name} 
                        secondary={attraction.description}
                        primaryTypographyProps={{ fontWeight: 'bold', fontSize: { xs: '0.9rem', sm: '1rem' } }}
                        secondaryTypographyProps={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      />
                    </ListItem>
                  ))}
                </List>
                <Divider sx={{ my: 4 }} />
                
                {/* Dining */}
                <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                  Dining Options
                </Typography>
                <List>
                  {details.dining.map((option, index) => (
                    <ListItem key={index} sx={{ pl: 0 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <RestaurantIcon color="primary" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={option.name} 
                        secondary={option.description}
                        primaryTypographyProps={{ fontWeight: 'bold', fontSize: { xs: '0.9rem', sm: '1rem' } }}
                        secondaryTypographyProps={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      />
                    </ListItem>
                  ))}
                </List>
                <Divider sx={{ my: 4 }} />
                
                {/* Accommodations */}
                <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                  Where to Stay
                </Typography>
                <List>
                  {details.accommodations.map((option, index) => (
                    <ListItem key={index} sx={{ pl: 0 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <HotelIcon color="primary" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={option.name} 
                        secondary={option.description}
                        primaryTypographyProps={{ fontWeight: 'bold', fontSize: { xs: '0.9rem', sm: '1rem' } }}
                        secondaryTypographyProps={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      />
                    </ListItem>
                  ))}
                </List>
                <Divider sx={{ my: 4 }} />
                
                {/* Travel Tips */}
                <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                  Travel Tips
                </Typography>
                <List>
                  {details.travelTips.map((tip, index) => (
                    <ListItem key={index} sx={{ pl: 0 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <PublicIcon color="primary" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={tip}
                        primaryTypographyProps={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      />
                    </ListItem>
                  ))}
                </List>
                
                {/* Plan Trip Button */}
                <Box sx={{ mt: 6, mb: 4, display: 'flex', justifyContent: 'center' }}>
                  <Button 
                    variant="contained" 
                    color="primary"
                    size="large"
                    startIcon={<FlightTakeoffIcon />}
                    component={RouterLink}
                    to={`/planner?destination=${encodeURIComponent(destination.name)}`}
                    sx={{ 
                      py: { xs: 1, sm: 1.5 }, 
                      px: { xs: 3, sm: 4 },
                      fontSize: { xs: '0.85rem', sm: '0.95rem' }
                    }}
                  >
                    Plan Your Trip to {destination.name.split(',')[0]}
                  </Button>
                </Box>
              </Box>
            </Grid>
            
            {/* Sidebar */}
            <Grid item xs={12} md={4}>
              {/* When to Visit */}
              <Paper elevation={0} sx={{ p: 3, mb: 4, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  When to Visit
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <CalendarMonthIcon color="primary" sx={{ mr: 1, mt: 0.5, fontSize: { xs: '1.1rem', sm: '1.25rem' } }} />
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                      Best Time
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                      {destination.bestTime}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                  During these months, you'll experience ideal weather conditions and can fully enjoy all the attractions {destination.name} has to offer.
                </Typography>
              </Paper>
              
              {/* Related Destinations */}
              <Paper elevation={0} sx={{ p: 3, mb: 4, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  Similar Destinations
                </Typography>
                
                {relatedDestinations.map(related => (
                  <Box key={related.id} sx={{ mb: 3, pb: 3, borderBottom: relatedDestinations.indexOf(related) !== relatedDestinations.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                    <Typography 
                      variant="subtitle1" 
                      fontWeight="medium"
                      component={RouterLink}
                      to={`/destinations/${related.id}`}
                      sx={{ 
                        textDecoration: 'none', 
                        color: 'text.primary',
                        display: 'block',
                        mb: 1,
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                        '&:hover': { color: 'primary.main' } 
                      }}
                    >
                      {related.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Rating value={related.rating} precision={0.1} size="small" readOnly />
                      <Typography variant="body2" sx={{ ml: 1, fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
                        {related.rating}
                      </Typography>
                    </Box>
                  </Box>
                ))}
                
                <Button 
                  variant="outlined" 
                  fullWidth
                  component={RouterLink}
                  to="/destinations"
                  sx={{ 
                    mt: 2,
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    py: { xs: 0.75, sm: 1 }
                  }}
                >
                  View All Destinations
                </Button>
              </Paper>
            </Grid>
          </Grid>
        </motion.div>
      </Box>
    </Container>
  );
};

export default DestinationDetailPage; 